import { Component, computed, effect, inject, input, OnInit, signal, Signal, WritableSignal } from '@angular/core';
import { SaComponent } from 'smartauction-frontend-micro-library';
import { ConfigurationState, InventoryManagementState } from 'remarketing-angular-library';
import { select, Store } from '@ngxs/store';
import { FormsModule } from '@angular/forms';
import { SearchFilterSectionComponent } from '../../search-filter/search-filter-section/search-filter-section.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslocoDirective } from '@jsverse/transloco';

import { SearchFilterService } from 'src/inventory-management/services/search-filter/search-filter.service';
import { AccountManager, Dealer } from 'src/inventory-management/interfaces/dealer-group.interface';
import { AccountManagerEmployeeAggregation, AccountManagerEmployeeAggregationDealerMap } from 'src/inventory-management/enums/account-manager.enum';

@Component({
  selector: 'app-account-manager-dealer-filter',
  imports: [SearchFilterSectionComponent, MatCheckboxModule, TranslocoDirective, FormsModule],
  templateUrl: './account-manager-dealer-filter.component.html',
  styleUrl: './account-manager-dealer-filter.component.scss'
})
export class AccountManagerDealerFilterComponent extends SaComponent {

  readonly accountManagerDealerFilterType$ = input.required<AccountManagerEmployeeAggregation>();

  protected readonly tenantName$ = select(ConfigurationState.tenantName);

  private readonly searchFilterService = inject(SearchFilterService);
  private readonly store = inject(Store);

  accountManagerDropdownOpen = new Map<string, boolean>();

  selectedDealers: Record<string, string[] | null> = {};

  /** Search terms for each account manager, keyed by account manager UUID */
  protected searchTerms: Record<string, WritableSignal<string>> = {};

  /** Filtered dealers for each account manager based on search term */
  protected filteredDealersMap: Record<string, Signal<Dealer[]>> = {};

  /** Visible count for each account manager, keyed by account manager UUID */
  protected visibleCounts: Record<string, WritableSignal<number>> = {};

  /** Number of items to show per page */
  private readonly itemsPerPage = 10;
  protected readonly searchThreshold = 10;

  constructor() {
    super();
    effect(() => {
      if (this.searchFilterService.getAccountManagerEmployees(this.accountManagerDealerFilterType$())()) {
        this.initializeSelectedDealers();
      }
    });
  }

  get dealersByAccountManager(): AccountManager[] {
    const sortedAccountManagers = this.searchFilterService.getAccountManagerEmployees(this.accountManagerDealerFilterType$())().sort((a, b) => {
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });
    return sortedAccountManagers;
  }

  accountManagerTerm(accountManager: AccountManager) {
    if (!accountManager[AccountManagerEmployeeAggregationDealerMap[this.accountManagerDealerFilterType$()]]) {
      return undefined;
    }
    return accountManager[AccountManagerEmployeeAggregationDealerMap[this.accountManagerDealerFilterType$()]];
  }

  private initializeSelectedDealers(): void {
    // Initialize all account managers with null (select all state)
    const currentSelectedDealers = this.store.selectSnapshot(InventoryManagementState.searchCriteria)?.filterByEmployeeDealerMap;
    this.dealersByAccountManager.forEach((accountManager) => {
      if (currentSelectedDealers && currentSelectedDealers[accountManager.value] !== null && currentSelectedDealers[accountManager.value].length > 0) {
        this.selectedDealers[accountManager.value] = currentSelectedDealers[accountManager.value];
      } else {
        this.selectedDealers[accountManager.value] = null;
      }
      this.initializeSearchForAccountManager(accountManager);
    });
  }

  private initializeSearchForAccountManager(accountManager: AccountManager): void {
    // Initialize search term signal for this account manager
    this.searchTerms[accountManager.value] = signal('');

    // Initialize visible count signal for this account manager
    this.visibleCounts[accountManager.value] = signal(this.itemsPerPage);

    // Create computed signal for filtered dealers
    this.filteredDealersMap[accountManager.value] = computed(() => {
      const searchTerm = this.searchTerms[accountManager.value]();
      if (!searchTerm) {
        return accountManager[AccountManagerEmployeeAggregationDealerMap[this.accountManagerDealerFilterType$()]];
      }
      return accountManager[AccountManagerEmployeeAggregationDealerMap[this.accountManagerDealerFilterType$()]].filter((dealer) =>
        dealer.name.toLowerCase().includes(searchTerm)
      );
    });
  }

  getAccountManagerTerm(accountManager: AccountManager) {
    return accountManager[AccountManagerEmployeeAggregationDealerMap[this.accountManagerDealerFilterType$()]];
  }

  getFilteredDealers(accountManagerUuid: string): Dealer[] {
    const filteredSignal = this.filteredDealersMap[accountManagerUuid];
    return filteredSignal ? filteredSignal() : [];
  }

  getSearchTerm(accountManagerUuid: string): string {
    const searchTermSignal = this.searchTerms[accountManagerUuid];
    return searchTermSignal ? searchTermSignal() : '';
  }

  search(event: Event, accountManagerUuid: string): void {
    // prevent search bar change events from bubbling up to parent components and causing unintended interactions
    event.stopPropagation();
    const target = event.target as HTMLInputElement;
    const searchTermSignal = this.searchTerms[accountManagerUuid];
    if (searchTermSignal) {
      searchTermSignal.set(target.value.toLowerCase().trim());
    }
    // Reset visible count when searching
    const visibleCountSignal = this.visibleCounts[accountManagerUuid];
    if (visibleCountSignal) {
      visibleCountSignal.set(this.itemsPerPage);
    }
  }

  clearSearch(event: MouseEvent, accountManagerUuid: string): void {
    // prevent search bar change events from bubbling up to parent components and causing unintended interactions
    event.stopPropagation();
    const searchTermSignal = this.searchTerms[accountManagerUuid];
    if (searchTermSignal) {
      searchTermSignal.set('');
    }
    // Reset visible count when clearing search
    const visibleCountSignal = this.visibleCounts[accountManagerUuid];
    if (visibleCountSignal) {
      visibleCountSignal.set(this.itemsPerPage);
    }
  }

  isAccountManagerDropdownOpen(accountManagerUuid: string): boolean {
    return this.accountManagerDropdownOpen.get(accountManagerUuid) ?? false;
  }

  toggleAccountManagerDropdown(accountManagerUuid: string): void {
    const accountManagerToggleState = this.isAccountManagerDropdownOpen(accountManagerUuid);
    const newState = !accountManagerToggleState;
    this.accountManagerDropdownOpen.set(accountManagerUuid, newState);

    // Clear search term and reset visible count when closing the dropdown
    if (!newState) {
      const searchTermSignal = this.searchTerms[accountManagerUuid];
      if (searchTermSignal) {
        searchTermSignal.set('');
      }
      const visibleCountSignal = this.visibleCounts[accountManagerUuid];
      if (visibleCountSignal) {
        visibleCountSignal.set(this.itemsPerPage);
      }
    }
  }

  toggleEmployee(accountManagerUuid: string, dealerUuid: string): void {
    const accountManager = this.dealersByAccountManager.find((am) => am.value === accountManagerUuid);
    if (!accountManager) return;

    const allDealerUuids = accountManager[AccountManagerEmployeeAggregationDealerMap[this.accountManagerDealerFilterType$()]].map((d) => d.value);
    let currentSelection = this.selectedDealers[accountManagerUuid];

    // If null (no explicit selections), start with the toggled dealer
    if (currentSelection === null) {
      currentSelection = [dealerUuid];
    } else if (currentSelection.includes(dealerUuid)) {
      // Remove dealer from selection
      currentSelection = currentSelection.filter((uuid) => uuid !== dealerUuid);
    } else {
      // Add dealer to selection
      currentSelection = [...currentSelection, dealerUuid];
    }

    // If no dealers are selected, reset to null (all dealers returned by backend)
    if (currentSelection.length === 0) {
      this.selectedDealers[accountManagerUuid] = null;
    } else {
      this.selectedDealers[accountManagerUuid] = currentSelection;
    }

    this.updateFilter();
  }

  private areAllDealersSelected(currentSelection: string[], allDealerUuids: string[]): boolean {
    if (currentSelection.length !== allDealerUuids.length) return false;
    return allDealerUuids.every((uuid) => currentSelection.includes(uuid));
  }

  toggleSelectAll(accountManagerUuid: string): void {
    const accountManager = this.dealersByAccountManager.find((am) => am.value === accountManagerUuid);
    if (!accountManager) return;

    const filteredDealers = this.getFilteredDealers(accountManagerUuid);
    const filteredDealerUuids = filteredDealers.map((d) => d.value);
    const currentSelection = this.selectedDealers[accountManagerUuid];

    // Get currently selected dealers (if null, none are visually selected)
    const currentlySelectedUuids = currentSelection ?? [];

    // Check if all filtered dealers are currently selected
    const allFilteredSelected = filteredDealerUuids.length > 0
      && filteredDealerUuids.every((uuid) => currentlySelectedUuids.includes(uuid));

    if (allFilteredSelected) {
      // Deselect all filtered dealers, keep others
      const newSelection = currentlySelectedUuids.filter((uuid) => !filteredDealerUuids.includes(uuid));
      // If no dealers remain selected, reset to null
      this.selectedDealers[accountManagerUuid] = newSelection.length === 0 ? null : newSelection;
    } else {
      // Select all filtered dealers, keep others
      const newSelection = [...new Set([...currentlySelectedUuids, ...filteredDealerUuids])];
      this.selectedDealers[accountManagerUuid] = newSelection;
    }

    this.updateFilter();
  }

  // Select All is only visually checked when every filtered dealer is explicitly present in the selection.
  // A null selection (meaning "all dealers" to the backend) is treated as visually unchecked.
  isSelectAllChecked(accountManagerUuid: string): boolean {
    const currentSelection = this.selectedDealers[accountManagerUuid];

    if (!currentSelection?.length) {
      return false;
    }

    const filteredDealers = this.getFilteredDealers(accountManagerUuid);
    const filteredDealerUuids = filteredDealers.map((d) => d.value);

    return filteredDealerUuids.every((uuid) => currentSelection.includes(uuid));
  }

  // A dealer is only visually checked when its UUID is explicitly present in the selection.
  // A null selection (meaning "all dealers" to the backend) is treated as visually unchecked.
  isDealerChecked(accountManagerUuid: string, dealerUuid: string): boolean {
    const currentSelection = this.selectedDealers[accountManagerUuid];

    if (!currentSelection?.length) {
      return false;
    }

    return currentSelection.includes(dealerUuid);
  }

  private updateFilter(): void {
    const hasAnyExplicitSelection = Object.values(this.selectedDealers).some(selection => selection !== null);

    if (hasAnyExplicitSelection) {
      // When any account manager has explicit selections, convert null entries to empty arrays
      // so the backend doesn't return results for account managers with no checkboxes selected.
      const filterPayload: Record<string, string[]> = {};
      for (const [key, value] of Object.entries(this.selectedDealers)) {
        filterPayload[key] = value ?? [];
      }
      this.searchFilterService.setEmployeeDealerMapFilter(filterPayload);
    } else {
      // When no selections exist anywhere (all null), send as-is so backend returns all results.
      this.searchFilterService.setEmployeeDealerMapFilter(this.selectedDealers);
    }
  }

  /** Get visible dealers for an account manager (limited by visible count) */
  getVisibleDealers(accountManagerUuid: string): Dealer[] {
    const filteredDealers = this.getFilteredDealers(accountManagerUuid);
    const visibleCountSignal = this.visibleCounts[accountManagerUuid];
    const visibleCount = visibleCountSignal ? visibleCountSignal() : this.itemsPerPage;
    return filteredDealers.slice(0, visibleCount);
  }

  /** Check if there are more items to show */
  hasMoreItems(accountManagerUuid: string): boolean {
    const filteredDealers = this.getFilteredDealers(accountManagerUuid);
    const visibleCountSignal = this.visibleCounts[accountManagerUuid];
    const visibleCount = visibleCountSignal ? visibleCountSignal() : this.itemsPerPage;
    return filteredDealers.length > visibleCount;
  }

  /** Check if we can show less (visible count > itemsPerPage) */
  canShowLess(accountManagerUuid: string): boolean {
    const filteredDealers = this.getFilteredDealers(accountManagerUuid);
    const visibleCountSignal = this.visibleCounts[accountManagerUuid];
    const visibleCount = visibleCountSignal ? visibleCountSignal() : this.itemsPerPage;
    return visibleCount > this.itemsPerPage && filteredDealers.length >= this.itemsPerPage;
  }

  /** Check if the toggle button should be shown */
  shouldShowToggleButton(accountManagerUuid: string): boolean {
    const filteredDealers = this.getFilteredDealers(accountManagerUuid);
    return filteredDealers.length >= this.itemsPerPage;
  }

  /** Show more items */
  showMore(event: MouseEvent, accountManagerUuid: string): void {
    // prevent search bar change events from bubbling up to parent components and causing unintended interactions
    event.stopPropagation();
    const visibleCountSignal = this.visibleCounts[accountManagerUuid];
    if (visibleCountSignal) {
      visibleCountSignal.update((count) => count + this.itemsPerPage);
    }
  }

  /** Show less items (reset to itemsPerPage) */
  showLess(event: MouseEvent, accountManagerUuid: string): void {
    // prevent search bar change events from bubbling up to parent components and causing unintended interactions
    event.stopPropagation();
    const visibleCountSignal = this.visibleCounts[accountManagerUuid];
    if (visibleCountSignal) {
      visibleCountSignal.set(this.itemsPerPage);
    }
  }

  stopPropagation(event: Event) {
    // prevent search bar change events from bubbling up to parent components and causing unintended interactions
    event.stopPropagation();
  }
}
